<template>
	<div class="dice-page">
		<van-nav-bar class="custom-nav-bar" :title="$t('task.page_title')">
			<template #left>
				<div style="width: 86px; height: 32px; margin-left: 15px;" @click="back()">
					<div class="nav-bar__back">
						<span class="tn-icon-left-arrow"></span>
					</div>
				</div>
			</template>
		</van-nav-bar>
		<div class="scroll-body">
			<div class="scroll-view-content">
				<div class="hero-card-wrap">
					<div class="hero-card">
						<div v-if="!hasSelectedProduct" class="hero-placeholder">
							<div class="hero-emoji">🎬</div>
							<div class="hero-tip-main">{{ $t('task.select_movie_tip') }}</div>
							<div class="hero-tip-sub">{{ $t('task.high_return_tip') }}</div>
						</div>

						<div v-else class="hero-selected">
							<div class="hero-selected-row">
								<div class="hero-thumb">
									<img :src="getAssetUrl(selectedProduct.img)"
										:alt="selectedProduct.name || ''" />
								</div>
								<div class="hero-info">
									<span class="hero-name">{{ selectedProduct.name }}</span>
									<span v-if="!hasSelectedContract" class="hero-no-contract">Bitte unten Rendite
										wählen</span>

									<div v-if="hasSelectedContract" class="hero-contract-row">
										<span class="hero-contract-badge">{{ selectedContract.name }}</span>
										<span class="hero-rate">{{ selectedContract.rate }}</span>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div class="section-block">
					<div class="section-header">
						<div class="header-line left"></div>
						<div class="header-content">
							<div class="section-icon">🎬</div>
							<div class="section-title-text">{{ $t('task.select_movie') }}</div>
						</div>
						<div class="header-line right"></div>
					</div>

					<div class="product-scroll">
						<div class="product-scroll-view">
							<div class="product-list">
								<div v-for="item in productList" :key="item.id" class="product-card-wrap"
									:class="{ 'product-active-wrap': selectedProduct && selectedProduct.id === item.id }"
									@click="selectProduct(item)">
									<div class="product-card">
										<img class="product-img" :src="getAssetUrl(item.img)"
											:alt="item.name || ''" />
										<div v-if="selectedProduct && selectedProduct.id === item.id"
											class="product-active-overlay">
											<span class="product-check">✓</span>
										</div>
									</div>
									<div class="product-name">{{ item.name }}</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div v-show="showPlayMode" class="section-block play-btn-block">
					<div class="section-header">
						<div class="header-line left"></div>
						<div class="header-content">
							<div class="section-icon">🎫</div>
							<div class="section-title-text">{{ $t('task.choose_mode') }}</div>
						</div>
						<div class="header-line right"></div>
					</div>
					<div class="play-btn-row">
						<div class="play-btn-item play-btn-on" @click.stop="openOrderPopup('open')">
							<span class="play-btn-label">{{ $t('task.ticket_open') }}</span>
						</div>
						<div class="play-btn-item play-btn-off" @click.stop="openOrderPopup('close')">
							<span class="play-btn-label">{{ $t('task.ticket_close') }}</span>
						</div>
					</div>
				</div>

				<div class="section-block">
					<div class="section-header clickable">
						<div class="header-line left"></div>
						<div class="header-content">
							<div class="section-icon">📊</div>
							<div class="section-title-text">{{ $t('task.my_records') }}</div>
						</div>
						<div class="header-line right"></div>
						<div class="toggle-arrow" @click="toggleBetList">{{ showBetList ? '▲' : '▼' }}</div>
					</div>

					<div v-if="showBetList && historyLoading" class="loading-row">
						<span class="loading-text">{{ $t('common.loading') }}</span>
					</div>
					<div v-else-if="showBetList && betHistory.length" class="bet-list">
						<div v-for="item in betHistory" :key="item.id" class="bet-record">
							<div class="br-left">
								<span class="br-product">{{ item.goods_name || selectedProduct.name || '-' }}</span>
								<span class="br-detail">{{ getHistoryDetailText(item) }}</span>
								<span class="br-time">{{ item.create_time_text || formatTime(item.create_time) }}</span>
							</div>
							<div class="br-right">
								<span class="br-status" :class="getHistoryStatusClass(item.status)">
									{{ item.status_text || getHistoryStatusText(item.status) }}
								</span>
								<span class="br-profit">{{ formatProfit(item.profit) }}</span>
							</div>
						</div>

						<div class="pagination-bar">
							<div class="page-btn" :class="{ 'page-btn-dis': historyPage <= 1 || historyLoading }"
								@click="changeHistoryPage(-1)">
								<span class="page-btn-text">‹</span>
							</div>
							<div class="page-info">{{ historyPage }} / {{ historyTotalPages }}</div>
							<div class="page-btn"
								:class="{ 'page-btn-dis': historyPage >= historyTotalPages || historyLoading }"
								@click="changeHistoryPage(1)">
								<span class="page-btn-text">›</span>
							</div>
						</div>
					</div>
					<div v-else-if="showBetList" class="empty-hint-full">
						Keine Aufzeichnungen
					</div>
				</div>

				<div class="btm-spacer"></div>
			</div>
		</div>
		<van-popup v-model="showOpenPopup" round position="bottom">
			<div class="order-pop-wrap">
				<div class="order-pop-header">
					<div class="order-pop-title-row">
						<span class="order-pop-title">{{ $t('task.ticket_open') }}</span>
						<div class="order-pop-close" @click="showOpenPopup = false">
							<span class="order-pop-close-icon">✕</span>
						</div>
					</div>
					<div class="order-pop-product-row">
						<img class="order-pop-thumb" :src="getAssetUrl(selectedProduct.img)"
							:alt="selectedProduct.name || ''" />
						<span class="order-pop-product-name">{{ selectedProduct.name }}</span>
					</div>
				</div>
				<div class="order-pop-section">
					<span class="order-pop-label">{{ $t('task.choose_return_rate') }}</span>
					<div class="order-contract-list">
						<div v-for="item in contractOptions" :key="`open-${item.name}`" class="order-contract-item"
							:class="{ 'order-contract-active': isSelectedContract(item) }"
							@click="selectContract(item)">
							<span class="oci-name">{{ item.name }}</span>
							<span class="oci-rate">{{ item.rate }}</span>
						</div>
					</div>
				</div>
				<div class="order-pop-section">
					<span class="order-pop-label">{{ $t('task.boost_amount') }}</span>
					<div class="order-amount-row">
						<div class="order-amount-btn" @click="changeOrderAmount(-100)">
							<span class="amount-sym">−</span>
						</div>
						<div class="order-amount-input-box">
							<div class="input">
								<div class="input__input">
									<input :value="orderAmount" type="number" min="0" :placeholder="$t('task.boost_amount')"
										@input="handleAmountInput($event.target.value)" />
								</div>
							</div>
						</div>
						<div class="order-amount-btn" @click="changeOrderAmount(100)">
							<span class="amount-sym">+</span>
						</div>
					</div>
					<span v-if="showProfitHint" class="order-profit-hint">
						Erwarteter Gewinn: <span class="profit-green">{{ formatProfit(computedProfit) }}</span>
					</span>
				</div>
				<div class="order-pop-footer">
					<div class="order-btn-cancel" @click="showOpenPopup = false">
						Abbrechen
					</div>
					<div class="order-btn-confirm" @click="openConfirmPopup">
						Bestätigen
					</div>
				</div>
			</div>
		</van-popup>
		<van-popup v-model="showClosePopup" round position="bottom">
			<div class="order-pop-wrap">
				<div class="order-pop-header">
					<div class="order-pop-title-row">
						<span class="order-pop-title">{{ $t('task.ticket_close') }}</span>
						<div class="order-pop-close" @click="showClosePopup = false">
							<span class="order-pop-close-icon">✕</span>
						</div>
					</div>
					<div class="order-pop-product-row">
						<img class="order-pop-thumb" :src="getAssetUrl(selectedProduct.img)"
							:alt="selectedProduct.name || ''" />
						<span class="order-pop-product-name">{{ selectedProduct.name }}</span>
					</div>
				</div>
				<div class="order-pop-section">
					<span class="order-pop-label">{{ $t('task.choose_return_rate') }}</span>
					<div class="order-contract-list">
						<div v-for="item in contractOptions" :key="`close-${item.name}`" class="order-contract-item"
							:class="{ 'order-contract-active': isSelectedContract(item) }"
							@click="selectContract(item)">
							<span class="oci-name">{{ item.name }}</span>
							<span class="oci-rate">{{ item.rate }}</span>
						</div>
					</div>
				</div>
				<div class="order-pop-section">
					<span class="order-pop-label">{{ $t('task.boost_amount') }}</span>
					<div class="order-amount-row">
						<div class="order-amount-btn" @click="changeOrderAmount(-100)">
							<span class="amount-sym">−</span>
						</div>
						<div class="order-amount-input-box">
							<div class="input">
								<div class="input__input">
									<input :value="orderAmount" type="number" min="0" :placeholder="$t('task.boost_amount')"
										@input="handleAmountInput($event.target.value)" />
								</div>
							</div>
						</div>
						<div class="order-amount-btn" @click="changeOrderAmount(100)">
							<span class="amount-sym">+</span>
						</div>
					</div>
					<span v-if="showProfitHint" class="order-profit-hint">
						Erwarteter Gewinn: <span class="profit-green">{{ formatProfit(computedProfit) }}</span>
					</span>
				</div>
				<div class="order-pop-footer">
					<div class="order-btn-cancel" @click="showClosePopup = false">
						Abbrechen
					</div>
					<div class="order-btn-confirm" @click="openConfirmPopup">
						Bestätigen
					</div>
				</div>
			</div>
		</van-popup>
		<van-popup v-model="showConfirmPopup" round>
			<div class="popup-wrap">
				<div class="popup-header">
					<span class="popup-icon-text">🎯</span>
					<span class="popup-title">{{ $t('task.confirm_boost') }}</span>
				</div>
				<div class="popup-body">
					<div class="popup-row">
						<span class="pop-label">{{ $t('common.product') }}</span>
						<span class="pop-val">{{ selectedProduct.name || '-' }}</span>
					</div>
					<div class="popup-divider"></div>
					<div class="popup-row">
						<span class="pop-label">{{ $t('task.play_mode') }}</span>
						<span class="pop-val">{{ currentTicketLabel }}</span>
					</div>
					<div class="popup-divider"></div>
					<div class="popup-row">
						<span class="pop-label">{{ $t('task.return_rate') }}</span>
						<span class="pop-val">{{ selectedContract.name || '-' }}</span>
					</div>
					<div class="popup-divider"></div>
					<div class="popup-row">
						<span class="pop-label">{{ $t('task.boost_amount') }}</span>
						<span class="pop-val pop-highlight">{{ formatCurrency(orderAmount) }}</span>
					</div>
					<div class="popup-divider"></div>
					<div class="popup-row">
						<span class="pop-label">{{ $t('task.expected_profit') }}</span>
						<span class="pop-val pop-green">{{ formatProfit(computedProfit) }}</span>
					</div>
				</div>
				<div class="popup-footer">
					<div class="pop-btn-cancel" @click="showConfirmPopup = false">
						<span class="pop-btn-text">{{ $t('common.cancel') }}</span>
					</div>
					<div class="pop-btn-ok" :class="{ 'pop-btn-disabled': submittingOrder }" @click="submitTaskOrder">
						<span class="pop-btn-text-ok">{{ submittingOrder ? $t('common.submitting') : $t('common.confirm') }}</span>
					</div>
				</div>
			</div>
		</van-popup>
	</div>
</template>

<script>
	export default {
		data() {
			return {
				productList: [],
				selectedProduct: {},
				selectedContract: {},
				pendingProductId: '',
				currentTicketType: 0,
				showPlayMode: false,
				showBetList: false,
				showOpenPopup: false,
				showClosePopup: false,
				showConfirmPopup: false,
				orderAmount: 0,
				betHistory: [],
				historyPage: 1,
				historyLimit: 10,
				historyTotalPages: 1,
				historyLoading: false,
				submittingOrder: false,
				contractOptions: [{
						name: 'Refund',
						rate: '+20.00%',
						rateValue: 20.00
					},
					{
						name: 'Preorder',
						rate: '+30.00%',
						rateValue: 30.00
					},
					{
						name: 'Half-price ticket',
						rate: '+40.00%',
						rateValue: 40.00
					},
					{
						name: 'Full price ticket',
						rate: '+50.00%',
						rateValue: 50.00
					}
				]
			};
		},
		computed: {
			hasSelectedProduct() {
				return !!(this.selectedProduct && this.selectedProduct.id);
			},
			hasSelectedContract() {
				return !!(this.selectedContract && this.selectedContract.name);
			},
			rateValue() {
				return this.parseRateValue(this.selectedContract);
			},
			computedProfit() {
				return Number((Number(this.orderAmount || 0) * this.rateValue / 100).toFixed(2));
			},
			showProfitHint() {
				return this.hasSelectedContract && Number(this.orderAmount) > 0;
			},
			currentTicketLabel() {
				return this.getTicketLabel(this.currentTicketType);
			}
		},
		watch: {
			'$route.query.productId': {
				handler(val) {
					this.applyProductIdSelection(val);
				},
				immediate: true
			}
		},
		mounted() {
			this.getProductList();
		},
		methods: {
			back() {
				return window.history.back();
			},
			getAssetUrl(url) {
				if (!url) {
					return '';
				}

				if (/^https?:\/\//i.test(url)) {
					return url;
				}

				return `https://shuadan-2.0.qpl-005.site${url}`;
			},
			getProductList() {
				this.$http({
					method: "get",
					url: "productList"
				}).then((res) => {
					if (res && res.code === 200) {
						this.productList = Array.isArray(res.data) ? res.data : [];
						this.applyProductIdSelection(this.pendingProductId || this.$route.query.productId);
						return;
					}

					this.productList = [];
					this.$toast(this.$t('task.product_list_load_failed'));
				});
			},
			applyProductIdSelection(productId) {
				if (!productId) {
					this.pendingProductId = '';
					return;
				}

				const id = Number(productId);
				if (!Number.isFinite(id) || id <= 0) {
					this.pendingProductId = '';
					return;
				}

				if (!Array.isArray(this.productList) || this.productList.length === 0) {
					this.pendingProductId = String(productId);
					return;
				}

				const target = this.productList.find((p) => Number(p.id) === id);
				if (!target) {
					this.pendingProductId = '';
					return;
				}

				this.pendingProductId = '';
				this.selectProduct(target);
			},
			async selectProduct(item) {
				if (!this.selectedProduct.id || this.selectedProduct.id !== item.id) {
					this.selectedContract = {};
					this.orderAmount = 0;
				}

				this.selectedProduct = item || {};
				this.showPlayMode = true;
				if (this.showBetList) {
					this.historyPage = 1;
					await this.getTaskOrderHistory(1);
				}
			},
			selectContract(item) {
				this.selectedContract = item || {};
			},
			isSelectedContract(item) {
				return this.selectedContract && this.selectedContract.name === item.name;
			},
			async toggleBetList() {
				this.showBetList = !this.showBetList;
				if (this.showBetList) {
					this.historyPage = 1;
					await this.getTaskOrderHistory(1);
				}
			},
			openOrderPopup(type) {
				if (!this.selectedProduct.id) {
					this.$toast(this.$t('task.select_product_first'));
					return;
				}

				this.currentTicketType = type === 'open' ? 1 : 2;
				if (type === 'open') {
					this.showOpenPopup = true;
					this.showClosePopup = false;
					return;
				}

				this.showClosePopup = true;
				this.showOpenPopup = false;
			},
			parseRateValue(contract) {
				if (contract && Number.isFinite(Number(contract.rateValue))) {
					return Number(Number(contract.rateValue).toFixed(2));
				}

				const value = Number(String((contract && contract.rate) || '').replace(/[^\d.-]/g, ''));
				return Number.isFinite(value) ? Number(value.toFixed(2)) : 0;
			},
			handleAmountInput(value) {
				const parsed = Number(value);
				this.orderAmount = Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
			},
			changeOrderAmount(step) {
				const currentValue = Number(this.orderAmount || 0);
				const nextValue = currentValue + step;
				this.orderAmount = nextValue > 0 ? nextValue : 0;
			},
			openConfirmPopup() {
				if (!this.hasSelectedProduct) {
					this.$toast(this.$t('task.select_product_first'));
					return;
				}

				if (!this.hasSelectedContract) {
					this.$toast(this.$t('task.select_contract_first'));
					return;
				}

				if (Number(this.orderAmount) <= 0) {
					this.$toast(this.$t('task.enter_amount'));
					return;
				}

				this.showConfirmPopup = true;
			},
			async submitTaskOrder() {
				if (this.submittingOrder) {
					return;
				}

				this.submittingOrder = true;
				try {
					const result = await this.$http({
						method: 'post',
						url: 'taskPlaceOrder',
						data: {
							goods_id: this.selectedProduct.id,
							amount: Number(this.orderAmount),
							return_rate: this.rateValue,
							contract_name: this.selectedContract.name,
							ticket_type: this.currentTicketType
						}
					});

					if (!result || ![1, 200].includes(Number(result.code))) {
						this.$toast((result && result.msg) || this.$t('task.create_order_failed'));
						return;
					}

					this.$toast((result && result.msg) || this.$t('task.create_order_success'));
					this.showConfirmPopup = false;
					this.showOpenPopup = false;
					this.showClosePopup = false;
					this.orderAmount = 0;

					if (this.showBetList) {
						this.historyPage = 1;
						await this.getTaskOrderHistory(1);
					}
				} catch (error) {
					console.error('任务下单失败:', error);
					this.$toast(this.$t('task.create_order_retry'));
				} finally {
					this.submittingOrder = false;
				}
			},
			async getTaskOrderHistory(page = this.historyPage) {
				this.historyLoading = true;
				try {
					const requestData = {
						page,
						limit: this.historyLimit
					};

					if (this.hasSelectedProduct) {
						requestData.goods_id = this.selectedProduct.id;
					}

					const result = await this.$http({
						method: 'get',
						url: 'taskOrderHistory',
						data: requestData
					});

					if (!result || ![1, 200].includes(Number(result.code))) {
						this.betHistory = [];
						this.historyTotalPages = 1;
						this.$toast((result && result.msg) || this.$t('task.order_record_load_failed'));
						return;
					}

					const payload = this.getHistoryPayload(result);
					this.betHistory = Array.isArray(payload.data) ? payload.data : [];
					this.historyPage = Number(payload.current_page || page || 1);
					this.historyTotalPages = Math.max(1, Number(payload.total_pages || 1));
				} catch (error) {
					console.error('获取任务订单记录失败:', error);
					this.betHistory = [];
					this.historyTotalPages = 1;
					this.$toast(this.$t('task.order_record_retry'));
				} finally {
					this.historyLoading = false;
				}
			},
			getHistoryPayload(result) {
				if (result && result.data2 && typeof result.data2 === 'object') {
					return result.data2;
				}

				if (result && result.data && typeof result.data === 'object' && !Array.isArray(result.data)) {
					return result.data;
				}

				return {};
			},
			changeHistoryPage(step) {
				if (this.historyLoading) {
					return;
				}

				const nextPage = this.historyPage + step;
				if (nextPage < 1 || nextPage > this.historyTotalPages) {
					return;
				}

				this.getTaskOrderHistory(nextPage);
			},
			getTicketLabel(ticketType) {
				return Number(ticketType) === 1 ? this.$t('task.ticket_open') : Number(ticketType) === 2 ? this.$t(
					'task.ticket_close') : '-';
			},
			getHistoryDetailText(item) {
				const ticketText = item.ticket_type_text || this.getTicketLabel(item.ticket_type);
				const contractName = item.contract_name || '-';
				const returnRateText = item.return_rate_text || (Number(item.return_rate) > 0 ?
					`+${Number(item.return_rate).toFixed(2)}%` : '-');
				return `${ticketText} ${contractName} · ${returnRateText}`;
			},
			getHistoryStatusText(status) {
				const normalized = Number(status);
				if (normalized === 1) {
					return this.$t('task.status_profit');
				}

				if (normalized === 2) {
					return this.$t('task.status_processed');
				}

				if (normalized === 3) {
					return this.$t('task.status_lost');
				}

				return this.$t('task.status_pending');
			},
			getHistoryStatusClass(status) {
				const normalized = Number(status);
				if (normalized === 1) {
					return 's-win';
				}

				if (normalized === 3) {
					return 's-lose';
				}

				return 's-pending';
			},
			formatCurrency(value) {
				return `€${Number(value || 0).toFixed(2)}`;
			},
			formatProfit(value) {
				return `+€${Number(value || 0).toFixed(2)}`;
			},
			formatTime(timeStr) {
				if (!timeStr) {
					return '-';
				}

				if (typeof timeStr === 'string' && timeStr.includes('-')) {
					return timeStr;
				}

				if (typeof timeStr === 'number') {
					const date = new Date(timeStr * 1000);
					const year = date.getFullYear();
					const month = String(date.getMonth() + 1).padStart(2, '0');
					const day = String(date.getDate()).padStart(2, '0');
					const hours = String(date.getHours()).padStart(2, '0');
					const minutes = String(date.getMinutes()).padStart(2, '0');
					const seconds = String(date.getSeconds()).padStart(2, '0');
					return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
				}

				return String(timeStr);
			}
		}
	}
</script>

<style>
	.dice-page {
		background: linear-gradient(180deg, #f8f9fa, #f2f2f5);
		min-height: 100vh
	}

	/* 导航栏返回按钮 */
	.custom-nav-bar {
		height: 45px;
		position: fixed;
		z-index: 999;
		display: block;
		position: relative;
		line-height: normal;
	}

	.custom-nav-bar .van-nav-bar__content {
		display: flex;
		align-items: center;
		justify-content: space-between;
		min-height: 0;
		height: 45px;
		padding-top: 0px;
		background-color: rgb(255, 255, 255);
		z-index: 29090;
		box-shadow: 0px 0px 41px 0px rgba(0, 0, 0, .05);
		position: fixed;
		width: 100%;
		top: 0;
	}

	.custom-nav-bar .van-nav-bar__left {
		padding: 0;
	}

	.custom-nav-bar .van-nav-bar__title {
		color: #000 !important;
		font-size: 18px;
		font-weight: 700;
		top: 0px;
		height: 45px;
		line-height: 45px;
		width: calc(100% - 100px);
		position: absolute;
		text-align: center;
		left: 0;
		right: 0;
		bottom: 0;
		cursor: none;
		text-overflow: ellipsis;
		white-space: nowrap;
		overflow: hidden;
	}

	.nav-bar__back {
		width: 31px;
		height: 31px;
		position: relative;
		display: flex;
		justify-content: center;
		align-items: center;
		box-sizing: border-box;
		background-color: rgba(0, 0, 0, .05);
		border-radius: 50%;
		color: #333;
		font-size: 14px;
	}

	.scroll-body {
		height: 100vh;
		box-sizing: border-box
	}

	/* 顶部英雄卡 */
	.hero-card-wrap {
		padding: 10px 15px
	}

	.hero-card {
		background: linear-gradient(135deg, #667eea, #764ba2);
		border-radius: 12px;
		padding: 20px;
		box-shadow: 0 4px 15px rgba(102, 126, 234, .3)
	}

	.hero-placeholder {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		padding: 5px 0
	}

	.hero-emoji {
		font-size: 29px;
		margin-bottom: 8px
	}

	.hero-tip-main {
		font-size: 16px;
		color: #fff;
		font-weight: 700;
		margin-bottom: 4px
	}

	.hero-tip-sub {
		font-size: 12px;
		color: hsla(0, 0%, 100%, .75)
	}

	.hero-selected-row {
		display: flex;
		align-items: center
	}

	.hero-thumb {
		width: 52px;
		height: 72px;
		border-radius: 6px;
		margin-right: 15px;
		flex-shrink: 0
	}

	.hero-thumb img {
		width: 100%;
		height: 100%;
	}

	.hero-thumb-placeholder {
		width: 52px;
		height: 72px;
		border-radius: 6px;
		background: hsla(0, 0%, 100%, .2);
		display: flex;
		align-items: center;
		justify-content: center;
		margin-right: 15px;
		font-size: 20px;
		flex-shrink: 0
	}

	.hero-info {
		flex: 1
	}

	.hero-name {
		display: block;
		font-size: 17px;
		font-weight: 700;
		color: #fff;
		margin-bottom: 8px
	}

	.hero-contract-row {
		display: flex;
		align-items: center;
		gap: 8px
	}

	.hero-contract-badge {
		background: hsla(0, 0%, 100%, .25);
		color: #fff;
		font-size: 11px;
		padding: 3px 8px;
		border-radius: 10px
	}

	.hero-rate {
		font-size: 15px;
		color: gold;
		font-weight: 700
	}

	.hero-no-contract {
		font-size: 12px;
		color: hsla(0, 0%, 100%, .65)
	}

	/* 区块通用 */
	.section-block {
		background: #fff;
		margin: 0 10px 12px;
		border-radius: 12px;
		padding: 16px 12px 12px;
		box-shadow: 0 2px 10px rgba(0, 0, 0, .06);
		border: 0.5px solid rgba(102, 126, 234, .07)
	}

	.section-header {
		display: flex;
		align-items: center;
		justify-content: center;
		margin-bottom: 14px;
		position: relative
	}

	.section-header.clickable {
		cursor: pointer
	}

	.header-line {
		flex: 1;
		height: 1px;
		max-width: 41px
	}

	.header-line.left {
		background: linear-gradient(90deg, transparent, #667eea)
	}

	.header-line.right {
		background: linear-gradient(270deg, transparent, #667eea)
	}

	.header-content {
		display: flex;
		align-items: center;
		padding: 0 10px
	}

	.section-icon {
		font-size: 15px;
		margin-right: 5px
	}

	.section-title-text {
		font-size: 15px;
		font-weight: 700;
		color: #333;
		letter-spacing: 1px
	}

	.toggle-arrow {
		position: absolute;
		right: 0;
		font-size: 11px;
		color: #999
	}

	/* 半票/全票 大按钮区 */
	.play-btn-block {
		padding-bottom: 16px
	}

	.play-btn-row {
		display: flex;
		gap: 12px
	}

	.play-btn-item {
		flex: 1;
		height: 52px;
		border-radius: 10px;
		display: flex;
		align-items: center;
		justify-content: center;
		box-shadow: 0 3px 10px rgba(0, 0, 0, .12)
	}

	.play-btn-on {
		background: linear-gradient(135deg, #27ae60, #2ecc71)
	}

	.play-btn-off {
		background: linear-gradient(135deg, #ee5a6f, #f093a0)
	}

	.play-btn-label {
		font-size: 17px;
		font-weight: 800;
		color: #fff;
		letter-spacing: 1px
	}

	/* 下单弹窗 */
	.order-pop-wrap {
		background: #fff;
		border-radius: 12px 12px 0 0;
		padding: 0 0 calc(15px + env(safe-area-inset-bottom))
	}

	.order-pop-header {
		padding: 18px 15px 12px;
		border-bottom: 0.5px solid #f2f2f5
	}

	.order-pop-title-row {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 8px
	}

	.order-pop-title {
		font-size: 18px;
		font-weight: 800;
		color: #333
	}

	.order-pop-close {
		width: 29px;
		height: 29px;
		border-radius: 50%;
		background: #f2f2f5;
		display: flex;
		align-items: center;
		justify-content: center
	}

	.order-pop-close-icon {
		font-size: 14px;
		color: #999
	}

	.order-pop-product-row {
		display: flex;
		align-items: center;
		gap: 8px
	}

	.order-pop-thumb {
		width: 33px;
		height: 45px;
		border-radius: 4px;
		flex-shrink: 0
	}

	.order-pop-product-name {
		font-size: 13px;
		color: #666
	}

	.order-pop-section {
		padding: 12px 15px 0
	}

	.order-pop-label {
		display: block;
		font-size: 13px;
		color: #999;
		margin-bottom: 8px
	}

	.order-contract-list {
		display: flex;
		flex-wrap: wrap;
		gap: 8px;
		margin-bottom: 4px
	}

	.order-contract-item {
		flex: 0 0 calc(33.33% - 6px);
		display: flex;
		flex-direction: column;
		align-items: center;
		padding: 10px 0;
		background: #f8f9fa;
		border-radius: 8px;
		border: 1px solid #ebebf5
	}

	.order-contract-active {
		background: linear-gradient(135deg, #667eea, #764ba2);
		border-color: transparent;
		box-shadow: 0 2px 8px rgba(102, 126, 234, .3)
	}

	.oci-name {
		font-size: 16px;
		font-weight: 800;
		color: #333;
		margin-bottom: 3px
	}

	.order-contract-active .oci-name {
		color: #fff
	}

	.oci-rate {
		font-size: 12px;
		font-weight: 700;
		color: #667eea
	}

	.order-contract-active .oci-rate {
		color: gold
	}

	.order-amount-row {
		display: flex;
		align-items: center;
		gap: 8px;
		margin-bottom: 6px
	}

	.order-amount-btn {
		width: 37px;
		height: 37px;
		background: linear-gradient(135deg, #667eea, #764ba2);
		border-radius: 50%;
		display: flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0
	}

	.order-amount-input-box {
		flex: 1;
		background: #f8f9fa;
		border-radius: 6px;
		padding: 3px 5px;
		border: 0.5px solid rgba(102, 126, 234, .15)
	}

	.order-amount-input-box .input {
		display: flex;
		flex-direction: row;
		position: relative;
		flex: 1;
		padding: 0px;
		border-color: rgb(220, 223, 230);
		text-align: center;
	}

	.order-amount-input-box .input .input__input {
		font-size: 14px;
		color: #080808;
		flex: 1;
		min-height: 36px;
	}

	.order-amount-input-box .input .input__input input {
		outline: none;
		border: none;
		padding: 0;
		margin: 0;
		text-decoration: inherit;
		display: -webkit-box;
		display: -webkit-flex;
		display: flex;
		position: relative;
		width: 100%;
		height: 100%;
		-webkit-box-orient: vertical;
		-webkit-box-direction: normal;
		-webkit-flex-direction: column;
		flex-direction: column;
		-webkit-box-pack: center;
		-webkit-justify-content: center;
		justify-content: center;
		width: 100%;
		position: relative;
		display: block;
		height: 100%;
		background: none;
		color: inherit;
		opacity: 1;
		font: inherit;
		line-height: inherit;
		letter-spacing: inherit;
		text-align: inherit;
		text-indent: inherit;
		text-transform: inherit;
		text-shadow: inherit;
	}

	.order-profit-hint {
		font-size: 11px;
		color: #888
	}

	.profit-green {
		color: #27ae60;
		font-weight: 700
	}

	.order-pop-footer {
		display: flex;
		gap: 10px;
		padding: 12px 15px 0
	}

	.order-btn-cancel {
		flex: 1;
		height: 45px;
		background: #f2f2f5;
		border-radius: 22px;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 14px;
		color: #666
	}

	.order-btn-confirm {
		flex: 2.5;
		height: 45px;
		background: linear-gradient(135deg, #667eea, #764ba2);
		border-radius: 22px;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 15px;
		font-weight: 700;
		color: #fff;
		box-shadow: 0 2px 8px rgba(102, 126, 234, .35)
	}

	/* 半票/全票 选择（旧，保留供确认弹窗用） */
	.ticket-row {
		display: flex;
		gap: 12px
	}

	.ticket-item {
		flex: 1;
		display: flex;
		flex-direction: column;
		align-items: center;
		padding: 14px 0;
		background: #f8f9fa;
		border-radius: 10px;
		border: 1px solid #ebebf5;
		transition: all .2s
	}

	.ticket-active {
		background: linear-gradient(135deg, #667eea, #764ba2);
		border-color: #667eea
	}

	.ticket-label {
		font-size: 16px;
		font-weight: 700;
		color: #333;
		margin-bottom: 4px
	}

	.ticket-active .ticket-label {
		color: #fff
	}

	.ticket-desc {
		font-size: 11px;
		color: #999
	}

	.ticket-active .ticket-desc {
		color: hsla(0, 0%, 100%, .8)
	}

	/* 产品横向滚动 */
	.product-scroll {
		white-space: nowrap
	}

	.product-scroll-view {
		overflow: auto hidden;
	}

	.product-list {
		display: inline-flex;
		gap: 10px;
		padding: 2px 2px 5px
	}

	.product-card-wrap {
		display: inline-flex;
		flex-direction: column;
		align-items: center;
		width: 83px;
		flex-shrink: 0;
		transition: -webkit-transform .2s;
		transition: transform .2s;
		transition: transform .2s, -webkit-transform .2s
	}

	.product-active-wrap {
		-webkit-transform: scale(1.05);
		transform: scale(1.05)
	}

	.product-card {
		width: 83px;
		height: 114px;
		border-radius: 8px;
		overflow: hidden;
		position: relative;
		box-shadow: 3px 3px 8px rgba(0, 0, 0, .1), -1px -1px 5px hsla(0, 0%, 100%, .8);
		transition: box-shadow .2s
	}

	.product-active-wrap .product-card {
		box-shadow: 0 0 0 1px #667eea, 3px 3px 8px rgba(102, 126, 234, .25)
	}

	.product-img {
		width: 100%;
		height: 100%
	}

	.no-img {
		background: linear-gradient(135deg, #f0f0f5, #e4e4ee);
		display: flex;
		align-items: center;
		justify-content: center;
		height: 100%
	}

	.no-img-icon {
		font-size: 26px
	}

	.product-active-overlay {
		position: absolute;
		inset: 0;
		background: rgba(102, 126, 234, .35);
		display: flex;
		align-items: center;
		justify-content: center
	}

	.product-check {
		font-size: 27px;
		color: #fff;
		font-weight: 700
	}

	.product-name {
		margin-top: 6px;
		font-size: 11px;
		color: #333;
		text-align: center;
		width: 83px;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap
	}

	.load-more-row {
		display: flex;
		justify-content: center;
		align-items: center;
		padding: 8px 0 2px
	}

	.load-more-text {
		font-size: 12px;
		color: #667eea;
		font-weight: 600;
		letter-spacing: 0.5px
	}

	/* ===== 合约横向4格滑动 ===== */
	.contract-scroll {
		width: 100%
	}

	.contract-list {
		display: flex;
		flex-direction: row;
		gap: 8px;
		padding: 2px 1px 4px
	}

	.contract-item {
		flex: 0 0 67px;
		min-width: 67px;
		display: flex;
		flex-direction: column;
		align-items: center;
		padding: 10px 4px;
		background: #f8f9fa;
		border-radius: 8px;
		border: 1px solid #ebebf5;
		transition: all .2s;
		cursor: pointer
	}

	.contract-item-active {
		background: linear-gradient(135deg, #667eea, #764ba2);
		border-color: transparent;
		box-shadow: 0 3px 10px rgba(102, 126, 234, .35)
	}

	.ci-period {
		font-size: 18px;
		font-weight: 800;
		color: #333;
		margin-bottom: 4px;
		letter-spacing: 0.5px
	}

	.contract-item-active .ci-period {
		color: #fff
	}

	.ci-rate {
		font-size: 14px;
		font-weight: 700;
		color: #667eea;
		margin-bottom: 4px
	}

	.contract-item-active .ci-rate {
		color: gold
	}

	.ci-range {
		font-size: 9px;
		color: #aaa;
		text-align: center
	}

	.contract-item-active .ci-range {
		color: hsla(0, 0%, 100%, .7)
	}

	/* 投注记录 */
	.bet-record {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 10px 0;
		border-bottom: 0.5px solid #f2f2f5
	}

	.br-left {
		display: flex;
		flex-direction: column
	}

	.br-product {
		font-size: 14px;
		color: #333;
		font-weight: 600;
		margin-bottom: 3px
	}

	.br-detail {
		font-size: 11px;
		color: #888;
		margin-bottom: 2px
	}

	.br-time {
		font-size: 10px;
		color: #bbb
	}

	.br-right {
		display: flex;
		flex-direction: column;
		align-items: flex-end
	}

	.br-status {
		font-size: 12px;
		font-weight: 600;
		padding: 3px 8px;
		border-radius: 10px;
		margin-bottom: 3px
	}

	.s-win {
		color: #27ae60;
		background: rgba(39, 174, 96, .12)
	}

	.s-lose {
		color: #ee5a6f;
		background: rgba(238, 90, 111, .12)
	}

	.s-pending {
		color: #667eea;
		background: rgba(102, 126, 234, .1)
	}

	.br-profit {
		font-size: 14px;
		color: #27ae60;
		font-weight: 700
	}

	.empty-hint {
		text-align: center;
		color: #bbb;
		font-size: 13px;
		padding: 10px 0
	}

	.empty-hint-full {
		text-align: center;
		color: #bbb;
		font-size: 13px;
		padding: 20px 0
	}

	.load-more {
		text-align: center;
		color: #667eea;
		font-size: 13px;
		padding: 12px 0
	}

	/* 翻页条 */
	.pagination-bar {
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 12px 0
	}

	.pagination-bar .page-btn {
		width: 33px;
		height: 33px;
		border-radius: 50%;
		background: #667eea;
		display: flex;
		align-items: center;
		justify-content: center
	}

	.pagination-bar .page-btn .page-btn-text {
		font-size: 18px;
		color: #fff;
		line-height: 1
	}

	.pagination-bar .page-btn.page-btn-dis {
		background: #ddd
	}

	.pagination-bar .page-btn.page-btn-dis .page-btn-text {
		color: #aaa
	}

	.pagination-bar .page-info {
		font-size: 14px;
		color: #667eea;
		font-weight: 600;
		margin: 0 16px
	}

	.loading-row {
		text-align: center;
		padding: 15px 0
	}

	.loading-text {
		color: #bbb;
		font-size: 13px
	}

	.btm-spacer {
		height: 31px
	}

	/* 底部面板样式已移除，改为下单弹窗 */
	/* 确认弹窗 */
	.popup-wrap {
		width: 301px;
		border-radius: 12px;
		overflow: hidden;
		background: #fff
	}

	.popup-header {
		background: linear-gradient(135deg, #667eea, #764ba2);
		padding: 20px 0 15px;
		text-align: center;
		display: flex;
		flex-direction: column;
		align-items: center
	}

	.popup-icon-text {
		font-size: 29px;
		margin-bottom: 5px
	}

	.popup-title {
		font-size: 16px;
		font-weight: 700;
		color: #fff
	}

	.popup-body {
		padding: 0 20px
	}

	.popup-row {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 12px 0
	}

	.popup-divider {
		height: 0.5px;
		background: #f2f2f5
	}

	.pop-label {
		font-size: 13px;
		color: #999
	}

	.pop-val {
		font-size: 14px;
		color: #333;
		font-weight: 600
	}

	.pop-highlight {
		color: #667eea;
		font-size: 16px
	}

	.pop-green {
		color: #27ae60;
		font-size: 16px
	}

	.popup-footer {
		display: flex;
		padding: 12px 20px 18px;
		gap: 10px
	}

	.pop-btn-cancel {
		flex: 1;
		height: 41px;
		background: #f2f2f5;
		border-radius: 20px;
		display: flex;
		align-items: center;
		justify-content: center
	}

	.pop-btn-text {
		font-size: 14px;
		color: #666
	}

	.pop-btn-ok {
		flex: 2;
		height: 41px;
		background: linear-gradient(135deg, #667eea, #764ba2);
		border-radius: 20px;
		display: flex;
		align-items: center;
		justify-content: center
	}

	.pop-btn-disabled {
		opacity: .6
	}

	.pop-btn-text-ok {
		font-size: 14px;
		font-weight: 700;
		color: #fff
	}

	/* ===== 倒计时弹窗 ===== */
	.cd-wrap {
		width: 291px;
		border-radius: 12px;
		overflow: hidden;
		background: #fff
	}

	/* 进行中 */
	.cd-header {
		background: linear-gradient(135deg, #667eea, #764ba2);
		padding: 18px 0 14px;
		display: flex;
		flex-direction: column;
		align-items: center
	}

	.cd-header-icon {
		font-size: 24px;
		margin-bottom: 4px
	}

	.cd-header-title {
		font-size: 15px;
		font-weight: 700;
		color: #fff
	}

	.cd-body {
		padding: 14px 20px
	}

	.cd-info-row {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 5px 0;
		border-bottom: 0.5px solid #f5f5f5
	}

	.cd-info-label {
		font-size: 12px;
		color: #999
	}

	.cd-info-val {
		font-size: 13px;
		color: #333;
		font-weight: 600
	}

	.cd-amount {
		color: #667eea
	}

	.cd-profit {
		color: #27ae60
	}

	.cd-timer-box {
		display: flex;
		justify-content: center;
		align-items: center;
		padding: 16px 0 10px
	}

	.cd-timer-ring {
		width: 93px;
		height: 93px;
		border-radius: 50%;
		background: linear-gradient(135deg, #667eea, #764ba2);
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		box-shadow: 0 4px 15px rgba(102, 126, 234, .4)
	}

	.cd-timer-num {
		font-size: 37px;
		font-weight: 800;
		color: #fff;
		line-height: 1
	}

	.cd-timer-unit {
		font-size: 12px;
		color: hsla(0, 0%, 100%, .8);
		margin-top: 2px
	}

	.cd-tip {
		display: block;
		text-align: center;
		font-size: 11px;
		color: #bbb;
		padding-bottom: 5px
	}

	/* 结果区 */
	.cd-result {
		display: flex;
		flex-direction: column;
		align-items: center
	}

	.cd-result-pending {
		display: flex;
		flex-direction: column;
		align-items: center;
		padding: 31px 20px
	}

	.cd-result-icon {
		font-size: 37px;
		margin-bottom: 10px
	}

	.cd-result-title {
		font-size: 16px;
		font-weight: 700;
		color: #333;
		margin-bottom: 6px
	}

	.cd-result-sub {
		font-size: 12px;
		color: #999
	}

	.cd-result-header {
		width: 100%;
		padding: 18px 0 14px;
		display: flex;
		flex-direction: column;
		align-items: center
	}

	.cd-win-header {
		background: linear-gradient(135deg, #27ae60, #2ecc71)
	}

	.cd-lose-header {
		background: linear-gradient(135deg, #ee5a6f, #c0392b)
	}

	.cd-result-header .cd-result-icon {
		font-size: 29px;
		margin-bottom: 5px
	}

	.cd-result-header .cd-result-title {
		font-size: 15px;
		font-weight: 700;
		color: #fff
	}

	.cd-result-body {
		width: 100%;
		padding: 12px 20px
	}

	.cd-res-row {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 7px 0;
		border-bottom: 0.5px solid #f5f5f5
	}

	.cd-res-label {
		font-size: 13px;
		color: #999
	}

	.cd-res-val {
		font-size: 14px;
		color: #333;
		font-weight: 600
	}

	.cd-res-green {
		color: #27ae60;
		font-size: 16px
	}

	.cd-res-red {
		color: #ee5a6f;
		font-size: 16px
	}

	.cd-close-btn {
		width: calc(100% - 41px);
		height: 41px;
		margin: 12px 20px 18px;
		background: linear-gradient(135deg, #667eea, #764ba2);
		border-radius: 20px;
		display: flex;
		align-items: center;
		justify-content: center
	}

	.cd-close-text {
		font-size: 15px;
		font-weight: 700;
		color: #fff
	}
</style>
